import React from 'react';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import lockTokens from '@amzn/meridian-tokens/base/icon/lock';
import styles from './styles.module.scss';

export interface ConsentGateHint {
  panelTitle: string;
  title: string;
  description: string;
}

const GatedPanel: React.FC<ConsentGateHint> = ({ panelTitle, title, description }) => (
  <div className={styles.root}>
    <div className={styles.header}>
      <Text type="b400" tag="span"><strong>{panelTitle}</strong></Text>
    </div>
    <div className={styles.body}>
      <div className={styles.icon}>
        <Icon tokens={lockTokens} />
      </div>
      <Text type="h300" color="primary">{title}</Text>
      <div className={styles.description}>
        <Text type="b200" color="secondary">{description}</Text>
      </div>
    </div>
  </div>
);

/**
 * Binds a panel and its consent hint, returning a selector `(gated) => Component`.
 * While gated the selector returns a component that shows the consent hint (with the panel
 * title preserved); otherwise it returns the original panel. Both are stable references, so
 * applying `gated` at render time never creates a new component type (no remount).
 * Call once at module scope, then apply `gated` in render: `const Panel = gate(isPending)`.
 */
export function withConsentGate<P extends object>(
  Component: React.ComponentType<P>,
  hint: ConsentGateHint,
): (gated: boolean) => React.ComponentType<P> {
  const Gated: React.FC<P> = () => <GatedPanel {...hint} />;
  Gated.displayName = `ConsentGated(${Component.displayName || Component.name || 'Component'})`;
  return (gated) => (gated ? Gated : Component);
}
